import React, { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
  useTheme
} from "@mui/material";

import * as Yup from "yup";
import { Formik } from "formik";
import { AuthContext } from "../../components/user/BCPAuthProvider";

import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import { postNotification } from "../../components/utilities/Notifications";

import moment from "moment";
import "moment-timezone";
import CountdownTimer from "../../components/utilities/CountdownTimer";

import AuthorizeHostedForm from "./components/SubComponents/AuthorizeHostedForm";

import {
  getData,
  getTicketingFeeSummary,
  numberWithCommas,
  postData,
  findGetParameter
} from "../../components/utilities/UtilityFunctions";
import { getTicketing } from "../../global/GlobalVariables";
import GenericPicker from "../../components/utilities/GenericPicker";
import { handlePagingAll } from "../../components/api/APIUtilities";

import {
  SquareCheckout,
  StripeCheckout
} from "./components/EventAbout/subcomponents/CheckoutForms";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { BCPRotateRight } from "../../components/icons/bcp/BCPIcons";

const IS_UY = getEnvironmentVariable("IS_UY") === "true";
const ENFORCE_ACCOUNT = getEnvironmentVariable("ENFORCE_ACCOUNT") === "true";

export default function EventRegistration() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const theme = useTheme();

  const { t } = useTranslation("translation", {
    keyPrefix: "components.playerRegistration"
  });

  const { t: tGeneral } = useTranslation();

  const embed = findGetParameter("embed") === "true";

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preLoading, setPreLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [cancel, setCancel] = useState(false);

  const [ticketModal, setTicketModal] = useState(1);
  const [teams, setTeams] = useState(null);
  const [playerData, setPlayerData] = useState({});
  const [paymentData, setPaymentData] = useState(null);
  const [transactionQueued, setTransactionQueued] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [transactionOrderId, setTransactionOrderId] = useState("");
  const [ticketingFeeSummary, setTicketingFeeSummary] = useState(null);

  const currency =
    Object.keys(event?.pricingDict || {})?.[0] ||
    event?.ticketing?.currency ||
    "usd";
  const ticketPrice =
    (event?.pricingDict?.[currency] ||
      event?.ticketPrice ||
      event?.ticketing?.price) / 100;

  useEffect(() => {
    if (!embed) {
      navigate(`/event/${eventId}`);
      return;
    }

    const fetchEvent = async () => {
      try {
        setLoading(true);
        const apiEvent = await getData(`/events/${eventId}`);

        if (authContext?.authState?.identity?.userId) {
          try {
            const playerObj = await getData(`/events/${eventId}/currentPlayer`);
            if (playerObj?.id) {
              postNotification(
                "info",
                "You are already registered for this event."
              );
              navigate(
                `/event/${eventId}?active_tab=${
                  apiEvent?.format?.teamEvent || apiEvent?.format?.doublesEvent
                    ? "team-details"
                    : "player-details"
                }`
              );
              return;
            }
          } catch {}
        }

        setEvent(apiEvent);
        setLoading(false);
      } catch (err) {
        postNotification("error", err);
        setLoading(false);
      }
    };

    if (eventId) {
      fetchEvent();
    }
  }, [eventId, embed, navigate, authContext]);

  const handleSetTeams = async (userId) => {
    if (userId) {
      try {
        const apiTeams = await handlePagingAll("/teams", {
          userId: userId,
          "expand[]": ["owner"]
        });

        let newTeams = [];
        if (apiTeams && apiTeams?.length) {
          apiTeams.sort((a, b) => {
            let teamA = a?.name?.toLowerCase();
            let teamB = b?.name?.toLowerCase();

            if (teamA > teamB) return 1;
            if (teamA < teamB) return -1;

            return 0;
          });

          apiTeams.forEach((team) =>
            newTeams.push({ label: team?.name, value: team?.id })
          );

          setTeams(newTeams);
        } else {
          setTeams(null);
        }
      } catch (err) {}
    } else {
      setTeams(null);
    }
  };

  useEffect(() => {
    if (!event) return;

    let ticketingDict = getTicketing();
    let _ticketingFeeSummary = getTicketingFeeSummary(
      1,
      ticketPrice,
      event?.ticketing?.playerPaysFees &&
        ticketingDict[currency?.toUpperCase()]?.FixedPlayer !== undefined
        ? ticketingDict[currency?.toUpperCase()]?.FixedPlayer
        : ticketingDict[currency?.toUpperCase()]?.Fixed,
      event?.ticketing?.playerPaysFees &&
        ticketingDict[currency?.toUpperCase()]?.PercentPlayer !== undefined
        ? ticketingDict[currency?.toUpperCase()]?.PercentPlayer
        : ticketingDict[currency?.toUpperCase()]?.Percent,
      !event?.ticketing?.playerPaysFees
    );

    setTicketingFeeSummary(_ticketingFeeSummary);

    const go = async () => {
      const initialPlayerData = {
        email: authContext?.authState?.identity?.email || "",
        firstName:
          authContext?.authState?.identity?.firstName ||
          authContext?.authState?.identity?.given_name ||
          "",
        lastName:
          authContext?.authState?.identity?.lastName ||
          authContext?.authState?.identity?.family_name ||
          "",
        teamName: "",
        teamId: "",
        accessCode: "",
        systemId: "",
        policy: false
      };

      if (authContext?.authState?.identity?.userId) {
        try {
          var apiUser = await getData(
            `/users/${authContext?.authState?.identity?.userId}`,
            {
              queryStringParameters: {
                "expand[]": ["team"]
              }
            }
          );
          if (apiUser?.team?.id) {
            initialPlayerData.teamId = apiUser?.team?.id;
          }
        } catch (err) {}

        try {
          await handleSetTeams(authContext?.authState?.identity?.userId);
        } catch (err) {}
      }
      setPlayerData(initialPlayerData);
      if (!transactionQueued) {
        if (event?.ticketing?.price > 0) {
          try {
            var apiTransactionQueued = await postData(
              `/transactions/preRegister`,
              {
                body: {
                  eventId: event?.id
                }
              }
            );

            setTransactionQueued(apiTransactionQueued);
            setPreLoading(false);

            await checkQueueStatus(apiTransactionQueued);
          } catch (err) {
            postNotification("error", err);
            cancelCheckout();
            return;
          }
        }
        setPreLoading(false);
      }
    };
    if (preLoading) go();
  }, [event, authContext]);

  const delay = (time) => {
    return new Promise((resolve) => setTimeout(resolve, time));
  };

  const submitPlayer = async (_playerData) => {
    setProcessing(true);
    var body = {
      eventId: event?.id,
      [event?.format?.teamEvent || event?.format?.doublesEvent
        ? "teamCaptain"
        : "user"]: {
        email: _playerData.email,
        firstName: _playerData.firstName,
        lastName: _playerData.lastName
      }
    };
    if (
      (event?.gameSystem?.useSystemId || event?.gameSystem?.systemIdRequired) &&
      !event?.format?.teamEvent &&
      !event?.format?.doublesEvent
    ) {
      body.systemId = _playerData.systemId;
      body.systemIdKey = event?.gameSystem?.systemIdName;
    }

    if (event?.format?.teamEvent || event?.format?.doublesEvent) {
      if (_playerData.teamName?.length > 0) {
        body.name = _playerData.teamName;
      }
    } else {
      if (_playerData.teamId?.length > 0) {
        body.teamId = _playerData.teamId;
      }
    }
    if (_playerData.accessCode?.length > 0) {
      body.accessCode = _playerData.accessCode;
    }
    if (Object.keys(_playerData.shippingDetails || {}).length > 0) {
      body.shippingDetails = _playerData.shippingDetails;
    }

    try {
      await postData(
        event?.format?.teamEvent || event?.format?.doublesEvent
          ? "/teamplayers"
          : "/players",
        {
          body: body
        }
      );

      postNotification(
        "success",
        tGeneral("notifications.success.registrationSuccess")
      );

      if (!authContext?.authState?.identity) {
        postNotification(
          "warning",
          `You must sign in to edit or view ${
            event?.format?.teamEvent || event?.format?.doublesEvent
              ? "team or"
              : ""
          } player details.`
        );
      }

      setTimeout(() => {
        window.location.assign(
          `/event/${event?.id}?active_tab=${
            event?.format?.teamEvent || event?.format?.doublesEvent
              ? "team-details"
              : "player-details"
          }`
        );
      }, [!authContext?.authState?.identity ? 5000 : 2000]);
    } catch (err) {
      postNotification("error", err);
      setProcessing(false);
    }
  };

  const checkQueueStatus = async (intitalTransactionQueued) => {
    await delay(5000);
    if (cancel) {
      return;
    }
    if (!intitalTransactionQueued?.id) {
      console.error("Error: no transaction to check queue against");
      return;
    }
    try {
      var apiTransactionQueued = await getData(
        `/transactions/${intitalTransactionQueued.id}`
      );

      if (intitalTransactionQueued?.authorizeNetKeys) {
        apiTransactionQueued.authorizeNetKeys =
          intitalTransactionQueued.authorizeNetKeys;
      }

      setTransactionQueued(apiTransactionQueued);
      if (
        apiTransactionQueued.queuedPosition !== undefined &&
        apiTransactionQueued.queuedPosition !==
          intitalTransactionQueued.queuedPosition
      ) {
        postNotification(
          "info",
          tGeneral("notifications.info.queuePositionUpdated")
        );
      } else if (
        apiTransactionQueued.status !== undefined &&
        apiTransactionQueued.status !== intitalTransactionQueued.status
      ) {
        if (intitalTransactionQueued.status === "Queued") {
          if (apiTransactionQueued.status === "Pending Checkout") {
            postNotification(
              "info",
              "A ticket has come available, please complete the player registration."
            );
          }
        }
      }
      if (
        apiTransactionQueued.status === "Queued" ||
        apiTransactionQueued.status === "Pending Checkout" ||
        apiTransactionQueued.status === "Pending Charge"
      ) {
        await checkQueueStatus(apiTransactionQueued);
        return;
      }
      if (intitalTransactionQueued.status === "Expired Checkout") {
        postNotification(
          "warning",
          tGeneral("notifications.warnings.playerRegistrationExpired")
        );
      } else if (intitalTransactionQueued.status === "Event Full") {
        postNotification(
          "warning",
          tGeneral("notifications.warnings.noTicketsRemaining")
        );
      }

      setTransactionQueued(null);
      navigate(`/event/${event?.id}`);
      return;
    } catch (err) {
      if (!cancel) {
        postNotification(
          "error",
          `Your ${
            event?.ticketing?.price > 0 ? "Purchase" : "Registration"
          } has been cancelled or expired.`
        );
      }
      setTransactionQueued(null);
      navigate(`/event/${event?.id}`);
    }
  };

  const cancelCheckout = async () => {
    setCancel(true);
    if (transactionQueued?.id) {
      setProcessing(true);
      try {
        await postData(`/transactions/${transactionQueued.id}/cancelCheckout`);
      } catch (err) {
        postNotification("error", err);
      }
    }
    setProcessing(false);
    setTransactionQueued(null);
    setPreLoading(true);
    navigate(`/event/${event?.id}`);
  };

  const PolicyComponent = () => {
    return (
      <>
        <Typography variant="body1" gutterBottom>
          {t("privacyDescription")}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {t("visitLinksFor")}{" "}
          <Link href="/privacy" target="_blank" underline="none">
            {t("privacy")}
          </Link>{" "}
          {t("policyAnd")}{" "}
          <Link href="/eula" target="_blank" underline="none">
            {t("eula")}
          </Link>
        </Typography>
        <Typography variant="body1">{t("contactOccasionally")}</Typography>
      </>
    );
  };

  if (loading || !event || !embed) {
    return <ModalLoadingFull />;
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1,
          p: 4,
          border: "1px solid rgba(0, 0, 0, 0.12)"
        }}
      >
        <Grid
          container
          spacing={1}
          sx={{
            mb: 1,
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
          }}
        >
          <Grid item xs={12}>
            <Typography variant="h4" gutterBottom>
              {event?.format?.teamEvent || event?.format?.doublesEvent
                ? t("teamRegistration")
                : t("playerRegistration")}{" "}
            </Typography>
            {transactionQueued?.queuedPosition === undefined &&
              event?.ticketing?.price > 0 && (
                <>
                  <Typography variant="caption" gutterBottom>
                    {t("fiveMinutesToComplete", {
                      type:
                        event?.ticketing?.price > 0
                          ? t("purchase")
                          : t("registration")
                    })}
                  </Typography>
                  {transactionQueued?.status === "Pending Charge" ? (
                    <CountdownTimer
                      timerData={{
                        startTime: moment(transactionQueued?.updated_at)
                          .utc()
                          .toISOString(),
                        endTime: moment(transactionQueued?.updated_at)
                          .add(5, "minutes")
                          .utc()
                          .toISOString()
                      }}
                      animateMinutes={1}
                    />
                  ) : (
                    <CountdownTimer
                      timerData={{
                        startTime: moment(transactionQueued?.updated_at)
                          .utc()
                          .toISOString(),
                        endTime: moment(transactionQueued?.updated_at)
                          .add(5, "minutes")
                          .utc()
                          .toISOString()
                      }}
                      animateMinutes={1}
                    />
                  )}
                </>
              )}
          </Grid>
        </Grid>
        <Box sx={{ minHeight: "400px" }}>
          {transactionQueued?.queuedPosition !== undefined && (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography variant="body1" gutterBottom>
                    {t("queueDescription", {
                      position: transactionQueued?.queuedPosition,
                      type:
                        event?.ticketing?.price > 0
                          ? t("purchase")
                          : t("registration")
                    })}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {t("yourQueuePosition")}
                  </Typography>
                  <Typography variant="h3" gutterBottom>
                    {transactionQueued?.queuedPosition === 0
                      ? t("next")
                      : transactionQueued?.queuedPosition}
                  </Typography>
                  <Typography
                    variant="body1"
                    gutterBottom
                    sx={{ color: "error.main" }}
                  >
                    {t("doNotCloseScreen")}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          )}
          {!preLoading && (
            <>
              <Formik
                initialValues={playerData}
                enableReinitialize
                validationSchema={
                  event?.format?.teamEvent || event?.format?.doublesEvent
                    ? Yup.object().shape({
                        email: Yup.string()
                          .max(128)
                          .email(t("validEmailRequired"))
                          .required(t("required")),
                        firstName: Yup.string()
                          .max(128)
                          .required(t("required")),
                        lastName: Yup.string().max(128).required(t("required")),
                        teamName: Yup.string().max(128).required(t("required")),
                        policy:
                          event?.ticketing?.price > 0
                            ? null
                            : Yup.boolean().oneOf([true], "Required"),
                        accessCode: event?.ticketing?.privateEvent
                          ? Yup.string().max(255).required("Required")
                          : null
                      })
                    : Yup.object().shape({
                        email: Yup.string()
                          .max(128)
                          .email(t("validEmailRequired"))
                          .required(t("required")),
                        firstName: Yup.string()
                          .max(128)
                          .required(t("required")),
                        lastName: Yup.string().max(128).required(t("required")),
                        teamId: Yup.string(),
                        policy:
                          event?.ticketing?.price > 0
                            ? null
                            : Yup.boolean().oneOf([true], "Required"),
                        accessCode: event?.ticketing?.privateEvent
                          ? Yup.string().max(255).required("Required")
                          : null,
                        systemId: event?.gameSystem?.systemIdRequired
                          ? Yup.string().required(t("required"))
                          : Yup.string()
                      })
                }
                onSubmit={async (values) => {
                  if (
                    event?.ticketing?.price > 0 ||
                    event?.ticketing?.shippingDetails?.requested
                  ) {
                    setPlayerData(values);
                    setTicketModal(event?.ticketing?.price > 0 ? 2 : 3);
                  } else {
                    await submitPlayer(values);
                  }
                  return;
                }}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  touched,
                  values,
                  setFieldValue
                }) => (
                  <form onSubmit={handleSubmit}>
                    {transactionQueued?.queuedPosition === undefined &&
                      ticketModal === 1 && (
                        <Grid container spacing={1}>
                          {(event?.format?.teamEvent ||
                            event?.format?.doublesEvent) && (
                            <Grid item xs={12}>
                              <TextField
                                name="teamName"
                                label={t("teamName")}
                                value={values.teamName}
                                variant="outlined"
                                fullWidth
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.teamName && errors.teamName
                                )}
                                helperText={touched.teamName && errors.teamName}
                                required
                              />
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <TextField
                              name="firstName"
                              label={`${
                                event?.format?.teamEvent ||
                                event?.format?.doublesEvent
                                  ? t("captainFirstName")
                                  : t("firstName")
                              }`}
                              value={values.firstName}
                              variant="outlined"
                              fullWidth
                              required
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.firstName && errors.firstName
                              )}
                              helperText={touched.firstName && errors.firstName}
                              disabled={ENFORCE_ACCOUNT}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="lastName"
                              label={`${
                                event?.format?.teamEvent ||
                                event?.format?.doublesEvent
                                  ? t("captainLastName")
                                  : t("lastName")
                              }`}
                              value={values.lastName}
                              variant="outlined"
                              fullWidth
                              required
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.lastName && errors.lastName
                              )}
                              helperText={touched.lastName && errors.lastName}
                              disabled={ENFORCE_ACCOUNT}
                            />
                          </Grid>

                          {(event?.gameSystem?.useSystemId ||
                            event?.gameSystem?.systemIdRequired) && (
                            <Grid item xs={12}>
                              <TextField
                                name="systemId"
                                label={
                                  event?.gameSystem?.systemIdName ||
                                  t("systemId")
                                }
                                value={values.systemId}
                                variant="outlined"
                                fullWidth
                                required={event?.gameSystem?.systemIdRequired}
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.systemId && errors.systemId
                                )}
                                helperText={touched.systemId && errors.systemId}
                              />
                            </Grid>
                          )}
                          {!IS_UY &&
                            !event?.format?.teamEvent &&
                            !event?.format?.doublesEvent && (
                              <Grid item xs={12}>
                                <GenericPicker
                                  name="teamId"
                                  label={t("clubTeamName")}
                                  value={values.teamId}
                                  variant="outlined"
                                  fullWidth
                                  margin="dense"
                                  onBlur={handleBlur}
                                  handleChange={handleChange}
                                  error={Boolean(
                                    touched.teamId && errors.teamId
                                  )}
                                  helperText={touched.teamId && errors.teamId}
                                  list={teams || []}
                                  inclAny={false}
                                  clearable
                                  endAdornment={
                                    <InputAdornment position="end">
                                      <Button
                                        sx={{ minWidth: "40px" }}
                                        onClick={async (e) => {
                                          setProcessing(true);
                                          await handleSetTeams(
                                            authContext?.authState?.identity
                                              ?.userId
                                          );
                                          setProcessing(false);
                                        }}
                                      >
                                        <BCPRotateRight />
                                      </Button>
                                    </InputAdornment>
                                  }
                                />
                                <FormHelperText>
                                  {t("createJoinClubTeam")}{" "}
                                  <Link
                                    href="/account?active_tab=teams"
                                    target="_blank"
                                  >
                                    {t("account")}
                                  </Link>
                                </FormHelperText>
                              </Grid>
                            )}
                          <Grid item xs={12}>
                            <TextField
                              name="email"
                              label={`${
                                getEnvironmentVariable("APP_NAME") || "BCP"
                              } ${t("accountEmail", {
                                type:
                                  event?.format?.teamEvent ||
                                  event?.format?.doublesEvent
                                    ? t("captain")
                                    : t("player")
                              })}`}
                              value={values.email}
                              variant="outlined"
                              fullWidth
                              required={event?.gameSystem?.emailRequired}
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(touched.email && errors.email)}
                              helperText={touched.email && errors.email}
                              disabled={ENFORCE_ACCOUNT}
                            />
                          </Grid>
                          {event?.ticketing?.privateEvent && (
                            <Grid item xs={12}>
                              <TextField
                                name="accessCode"
                                label={t("accessCode")}
                                value={values.accessCode}
                                variant="outlined"
                                fullWidth
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.accessCode && errors.accessCode
                                )}
                                helperText={
                                  touched.accessCode && errors.accessCode
                                }
                                required
                              />
                            </Grid>
                          )}
                          {!Boolean(event?.ticketing?.price > 0) && (
                            <>
                              <Grid item xs={12}>
                                <PolicyComponent />
                              </Grid>
                              <Grid item xs={12}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      name="policy"
                                      checked={values.policy}
                                      defaultChecked={false}
                                      onChange={(e) => {
                                        setFieldValue("policy", !values.policy);
                                      }}
                                    />
                                  }
                                  label={t("agreeToTerms")}
                                />

                                {Boolean(touched.policy && errors.policy) && (
                                  <FormHelperText error>
                                    {errors.policy}
                                  </FormHelperText>
                                )}
                              </Grid>
                            </>
                          )}
                          <Grid item xs={6}>
                            <Button
                              color="error"
                              fullWidth
                              variant="outlined"
                              onClick={cancelCheckout}
                            >
                              {t("cancel")}
                            </Button>
                          </Grid>

                          <Grid item xs={6}>
                            <Button
                              color="success"
                              fullWidth
                              variant="contained"
                              type="submit"
                            >
                              {event?.ticketing?.price > 0
                                ? t("next")
                                : t("register")}
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                  </form>
                )}
              </Formik>
              <Formik
                initialValues={
                  paymentData || {
                    email:
                      authContext?.authState?.identity?.email ||
                      playerData?.email ||
                      "",
                    firstName:
                      authContext?.authState?.identity?.firstName ||
                      authContext?.authState?.identity?.given_name ||
                      playerData?.firstName ||
                      "",
                    lastName:
                      authContext?.authState?.identity?.lastName ||
                      authContext?.authState?.identity?.family_name ||
                      playerData?.lastName ||
                      "",
                    couponCode: "",
                    policy: false,
                    acknowledgeRefund: false
                  }
                }
                enableReinitialize
                validationSchema={Yup.object().shape({
                  email: Yup.string()
                    .max(128)
                    .email(t("validEmailRequired"))
                    .required(t("required")),
                  firstName: Yup.string().max(128).required(t("required")),
                  lastName: Yup.string().max(128).required(t("required")),
                  couponCode: Yup.string().max(255),
                  policy: Yup.boolean().oneOf([true], t("required")),
                  acknowledgeRefund: event?.ticketing?.refundDate
                    ? Yup.boolean().oneOf([true], t("required"))
                    : Yup.boolean()
                })}
                onSubmit={async (values) => {
                  setPaymentData(values);
                  if (event?.ticketing?.shippingDetails?.requested) {
                    setTicketModal(3);
                  } else {
                    setProcessing(true);
                    setTicketModal(4);
                  }
                }}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  touched,
                  values,
                  setFieldValue
                }) => (
                  <form onSubmit={handleSubmit}>
                    {transactionQueued?.queuedPosition === undefined &&
                      ticketModal === 2 && (
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Typography variant="h5" gutterBottom>
                              {t("billingInfo")}
                            </Typography>
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="firstName"
                              label={t("firstName")}
                              value={values.firstName}
                              variant="outlined"
                              fullWidth
                              required
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.firstName && errors.firstName
                              )}
                              helperText={touched.firstName && errors.firstName}
                              disabled={ENFORCE_ACCOUNT}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="lastName"
                              label={t("lastName")}
                              value={values.lastName}
                              variant="outlined"
                              fullWidth
                              required
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.lastName && errors.lastName
                              )}
                              helperText={touched.lastName && errors.lastName}
                              disabled={ENFORCE_ACCOUNT}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="email"
                              label={t("receiptEmail")}
                              value={values.email}
                              variant="outlined"
                              fullWidth
                              required
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={(e) => {
                                const trimmedString = e.target.value
                                  .trim()
                                  .toLowerCase();
                                setFieldValue("email", trimmedString);
                              }}
                              error={Boolean(touched.email && errors.email)}
                              helperText={touched.email && errors.email}
                              disabled={ENFORCE_ACCOUNT}
                            />
                          </Grid>
                          {event?.chargeType !== "authorized" && (
                            <Grid item xs={12}>
                              <TextField
                                name="couponCode"
                                label={t("couponCode")}
                                value={values.couponCode}
                                variant="outlined"
                                fullWidth
                                margin="dense"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                error={Boolean(
                                  touched.couponCode && errors.couponCode
                                )}
                                helperText={
                                  touched.couponCode && errors.couponCode
                                }
                              />
                            </Grid>
                          )}
                          {ticketingFeeSummary?.amount > 0 && (
                            <>
                              <Grid item xs={6}>
                                <Typography paragraph>
                                  {t("ticketPrice")}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography paragraph>
                                  {numberWithCommas(
                                    ticketingFeeSummary?.total?.toFixed(2)
                                  )}{" "}
                                  {currency?.toUpperCase()}
                                </Typography>
                              </Grid>
                              {!ticketingFeeSummary?.absorbFees && (
                                <>
                                  <Grid item xs={6}>
                                    <Typography paragraph>
                                      {t("ticketFees")}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography paragraph>
                                      {numberWithCommas(
                                        ticketingFeeSummary?.fee?.toFixed(2)
                                      )}{" "}
                                      {currency?.toUpperCase()}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography paragraph>
                                      {t("totalAmount")}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Typography paragraph>
                                      {numberWithCommas(
                                        ticketingFeeSummary?.amount?.toFixed(2)
                                      )}{" "}
                                      {currency?.toUpperCase()}
                                    </Typography>
                                  </Grid>
                                </>
                              )}
                            </>
                          )}
                          <Grid item xs={12}>
                            <PolicyComponent />
                          </Grid>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  name="policy"
                                  checked={values.policy}
                                  defaultChecked={false}
                                  onChange={(e) => {
                                    setFieldValue("policy", !values.policy);
                                  }}
                                />
                              }
                              label={t("agreeToTerms")}
                            />

                            {Boolean(touched.policy && errors.policy) && (
                              <FormHelperText error>
                                {errors.policy}
                              </FormHelperText>
                            )}
                          </Grid>

                          {event?.ticketing?.refundDate && (
                            <Grid item xs={12}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    name="acknowledgeRefund"
                                    checked={values.acknowledgeRefund}
                                    defaultChecked={false}
                                    onChange={(e) => {
                                      setFieldValue(
                                        "acknowledgeRefund",
                                        !values.acknowledgeRefund
                                      );
                                    }}
                                  />
                                }
                                label={t("acknowledgeRefundDate", {
                                  date: moment(
                                    event?.ticketing?.refundDate
                                  ).format("M.D.YYYY LT")
                                })}
                              />

                              {Boolean(
                                touched.acknowledgeRefund &&
                                  errors.acknowledgeRefund
                              ) && (
                                <FormHelperText error>
                                  {errors.acknowledgeRefund}
                                </FormHelperText>
                              )}
                            </Grid>
                          )}

                          <Grid item xs={6}>
                            <Button
                              color="error"
                              fullWidth
                              variant="outlined"
                              onClick={(e) => {
                                setTicketModal(1);
                              }}
                            >
                              {t("back")}
                            </Button>
                          </Grid>
                          <Grid item xs={6}>
                            <Button
                              color="success"
                              fullWidth
                              variant="contained"
                              type="submit"
                            >
                              {t("next")}
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                  </form>
                )}
              </Formik>
              <Formik
                initialValues={{
                  firstName: playerData?.firstName || "",
                  lastName: playerData?.lastName || "",
                  email: playerData?.email || "",
                  company: "",
                  deliveryNotes: "",
                  address1: "",
                  address2: "",
                  city: "",
                  zip: "",
                  state: "",
                  country: ""
                }}
                enableReinitialize
                validationSchema={Yup.object().shape({
                  firstName: event?.ticketing?.shippingDetails?.mandatory
                    ? Yup.string().max(128).required("Required")
                    : Yup.string().max(128),
                  lastName: event?.ticketing?.shippingDetails?.mandatory
                    ? Yup.string().max(128).required("Required")
                    : Yup.string().max(128),
                  email: event?.ticketing?.shippingDetails?.mandatory
                    ? Yup.string()
                        .max(128)
                        .email("Must be a valid email")
                        .required("Required")
                    : Yup.string().max(128).email("Must be a valid email"),
                  company: Yup.string().max(255),
                  deliveryNotes: Yup.string(),
                  address1: event?.ticketing?.shippingDetails?.mandatory
                    ? Yup.string().max(255).required("Required")
                    : Yup.string().max(255),
                  address2: Yup.string().max(255),
                  city: event?.ticketing?.shippingDetails?.mandatory
                    ? Yup.string().max(255).required("Required")
                    : Yup.string().max(255),
                  zip: Yup.string().max(128),
                  state: Yup.string().max(255),
                  country: event?.ticketing?.shippingDetails?.mandatory
                    ? Yup.string().max(255).required("Required")
                    : Yup.string().max(255)
                })}
                onSubmit={async (values) => {
                  if (event?.ticketing?.price > 0) {
                    setProcessing(true);

                    setPlayerData({
                      ...playerData,
                      shippingDetails: values
                    });
                    setTicketModal(4);
                  } else {
                    await submitPlayer({
                      ...playerData,
                      shippingDetails: values
                    });
                  }
                }}
              >
                {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  touched,
                  values,
                  setFieldValue
                }) => (
                  <form onSubmit={handleSubmit}>
                    {transactionQueued?.queuedPosition === undefined &&
                      ticketModal === 3 && (
                        <Grid container spacing={1}>
                          <Grid item xs={12}>
                            <Typography variant="h5" gutterBottom>
                              {t("shippingDetails")}
                            </Typography>
                            {Boolean(
                              event?.ticketing?.shippingDetails?.description
                            ) && (
                              <Typography variant="caption" gutterBottom>
                                {event?.ticketing?.shippingDetails?.description}
                              </Typography>
                            )}
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="firstName"
                              label="First Name"
                              value={values.firstName}
                              variant="outlined"
                              fullWidth
                              required={
                                event?.ticketing?.shippingDetails?.mandatory
                                  ? true
                                  : false
                              }
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.firstName && errors.firstName
                              )}
                              helperText={touched.firstName && errors.firstName}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="lastName"
                              label="Last Name"
                              value={values.lastName}
                              variant="outlined"
                              fullWidth
                              required={
                                event?.ticketing?.shippingDetails?.mandatory
                                  ? true
                                  : false
                              }
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.lastName && errors.lastName
                              )}
                              helperText={touched.lastName && errors.lastName}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="email"
                              label="Email"
                              value={values.email}
                              variant="outlined"
                              fullWidth
                              required={
                                event?.ticketing?.shippingDetails?.mandatory
                                  ? true
                                  : false
                              }
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={(e) => {
                                const trimmedString = e.target.value
                                  .trim()
                                  .toLowerCase();
                                setFieldValue("email", trimmedString);
                              }}
                              error={Boolean(touched.email && errors.email)}
                              helperText={touched.email && errors.email}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="company"
                              label={t("company")}
                              value={values.company}
                              variant="outlined"
                              fullWidth
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(touched.company && errors.company)}
                              helperText={touched.company && errors.company}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              name="address1"
                              label={t("addressLine1")}
                              value={values.address1}
                              variant="outlined"
                              fullWidth
                              required={
                                event?.ticketing?.shippingDetails?.mandatory
                                  ? true
                                  : false
                              }
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.address1 && errors.address1
                              )}
                              helperText={touched.address1 && errors.address1}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="address2"
                              label={t("addressLine2")}
                              value={values.address2}
                              variant="outlined"
                              fullWidth
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.address2 && errors.address2
                              )}
                              helperText={touched.address2 && errors.address2}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="city"
                              label={t("city")}
                              value={values.city}
                              variant="outlined"
                              fullWidth
                              required={
                                event?.ticketing?.shippingDetails?.mandatory
                                  ? true
                                  : false
                              }
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(touched.city && errors.city)}
                              helperText={touched.city && errors.city}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="zip"
                              label={t("zipCode")}
                              value={values.zip}
                              variant="outlined"
                              fullWidth
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(touched.zip && errors.zip)}
                              helperText={touched.zip && errors.zip}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="state"
                              label={t("state")}
                              value={values.state}
                              variant="outlined"
                              fullWidth
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(touched.state && errors.state)}
                              helperText={touched.state && errors.state}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="country"
                              label={t("country")}
                              value={values.country}
                              variant="outlined"
                              fullWidth
                              required={
                                event?.ticketing?.shippingDetails?.mandatory
                                  ? true
                                  : false
                              }
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(touched.country && errors.country)}
                              helperText={touched.country && errors.country}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              name="deliveryNotes"
                              label={t("deliveryNotes")}
                              value={values.deliveryNotes}
                              variant="outlined"
                              fullWidth
                              margin="dense"
                              onBlur={handleBlur}
                              onChange={handleChange}
                              error={Boolean(
                                touched.deliveryNotes && errors.deliveryNotes
                              )}
                              helperText={
                                touched.deliveryNotes && errors.deliveryNotes
                              }
                              multiline
                              rows={3}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Button
                              color="error"
                              fullWidth
                              variant="outlined"
                              onClick={(e) => {
                                setTicketModal(
                                  event?.ticketing?.price > 0 ? 2 : 1
                                );
                              }}
                            >
                              {t("back")}
                            </Button>
                          </Grid>
                          <Grid item xs={6}>
                            <Button
                              color="success"
                              fullWidth
                              variant="contained"
                              type="submit"
                            >
                              {t("checkout")}
                            </Button>
                          </Grid>
                        </Grid>
                      )}
                  </form>
                )}
              </Formik>
              {transactionQueued?.queuedPosition === undefined &&
                ticketModal === 4 && (
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      {event?.ticketing?.chargeType === "authorized" ? (
                        <AuthorizeHostedForm
                          event={event}
                          transactionQueued={transactionQueued}
                          playerData={playerData}
                          paymentData={paymentData}
                          loading={processing}
                          setLoading={setProcessing}
                          setTicketModal={setTicketModal}
                        />
                      ) : event?.ticketing?.chargeType === "square" ? (
                        <SquareCheckout
                          event={event}
                          transactionQueued={transactionQueued}
                          playerData={playerData}
                          paymentData={paymentData}
                          loading={processing}
                          setLoading={setProcessing}
                          setTicketModal={setTicketModal}
                          clientSecret={clientSecret}
                          setClientSecret={setClientSecret}
                          transactionId={transactionId}
                          setTransactionId={setTransactionId}
                          transactionOrderId={transactionOrderId}
                          setTransactionOrderId={setTransactionOrderId}
                        />
                      ) : (
                        <StripeCheckout
                          event={event}
                          transactionQueued={transactionQueued}
                          playerData={playerData}
                          paymentData={paymentData}
                          loading={processing}
                          setLoading={setProcessing}
                          setTicketModal={setTicketModal}
                          cancelCheckout={cancelCheckout}
                          clientSecret={clientSecret}
                          setClientSecret={setClientSecret}
                          transactionId={transactionId}
                          setTransactionId={setTransactionId}
                          transactionOrderId={transactionOrderId}
                          setTransactionOrderId={setTransactionOrderId}
                        />
                      )}
                    </Grid>
                  </Grid>
                )}
            </>
          )}
        </Box>
      </Box>
      {(processing || preLoading) && <ModalLoadingFull />}
    </Container>
  );
}
